/**
 * Theremin - L'instrument joué sans le toucher !
 * Contrôlé par la position de la souris:
 * - X = Pitch (fréquence)
 * - Y = Volume
 */

import * as Tone from 'tone';

export class Theremin {
    constructor(options = {}) {
        this.performance = options.performance || { lowPower: false };
        this.isPowered = true;
        this.mixerChannel = null;
        this.isPlaying = false;

        this._params = {
            minFreq: 65,      // C2
            maxFreq: 1047,    // C6
            vibrato: 5,       // Hz - typique pour theremin
            vibratoAmount: 0.3, // Subtil par défaut (0-1)
            waveform: 'sine',
            portamento: 0.05,
            volume: 0.6
        };

        this._createSynth();
    }

    _createSynth() {
        // Oscillateur principal
        this.oscillator = new Tone.Oscillator({
            frequency: 440,
            type: this._params.waveform
        });

        // Vibrato (caractéristique du theremin)
        // LFO oscille entre -1 et +1, multiplié par vibratoGain pour donner ±N Hz
        this.vibrato = new Tone.LFO({
            frequency: this._params.vibrato,
            min: -1,
            max: 1,
            type: 'sine'
        }).start();

        // Gain contrôle l'amplitude du vibrato en Hz (max ~15 Hz de modulation)
        this.vibratoGain = new Tone.Gain(this._params.vibratoAmount * 15);
        this.vibrato.connect(this.vibratoGain);
        this.vibratoGain.connect(this.oscillator.frequency);

        // Volume envelope pour éviter les clics
        this.volumeGain = new Tone.Gain(0);

        // Filtre léger pour adoucir
        this.filter = new Tone.Filter({
            frequency: 3000,
            type: 'lowpass',
            rolloff: -12
        });

        // Sortie
        this.outputGain = new Tone.Gain(this._params.volume);

        // Chaîne audio
        this.oscillator.connect(this.filter);
        this.filter.connect(this.volumeGain);
        this.volumeGain.connect(this.outputGain);

        // État actuel
        this._currentFreq = 440;
        this._currentVol = 0;
    }

    connectTo(destination) {
        this.mixerChannel = destination;
        if (this.isPowered) {
            this.outputGain.connect(destination);
        }
    }

    setPower(on) {
        this.isPowered = on;
        if (this.mixerChannel) {
            if (on) {
                this.outputGain.connect(this.mixerChannel);
                if (this.performance.lowPower && !this.isPlaying) {
                    this._startOscillators();
                }
            } else {
                this.outputGain.disconnect();
                this.stop();
                if (this.performance.lowPower) {
                    this._stopOscillators();
                }
            }
        }
    }

    getPower() {
        return this.isPowered;
    }

    _startOscillators() {
        if (!this.isPlaying) {
            this.oscillator.start();
            this.isPlaying = true;
        }
    }

    _stopOscillators() {
        if (this.isPlaying) {
            this.oscillator.stop();
            this.isPlaying = false;
        }
    }

    /**
     * Démarre le theremin (appelé lors du clic)
     */
    start() {
        if (!this.isPowered || this.isPlaying) return;
        this.oscillator.start();
        this.isPlaying = true;
    }

    /**
     * Arrête le theremin
     */
    stop() {
        if (!this.isPlaying) return;
        this.volumeGain.gain.rampTo(0, 0.1);
        setTimeout(() => {
            if (this.isPlaying) {
                this.oscillator.stop();
                this.isPlaying = false;
            }
        }, 150);
    }

    /**
     * Met à jour le pitch et volume selon la position (0-1, 0-1)
     * @param {number} x - Position horizontale (0=grave, 1=aigu)
     * @param {number} y - Position verticale (0=fort, 1=silencieux)
     */
    updatePosition(x, y) {
        if (!this.isPowered) return;

        // Calcul de la fréquence (échelle logarithmique pour musical)
        const minLog = Math.log(this._params.minFreq);
        const maxLog = Math.log(this._params.maxFreq);
        const freq = Math.exp(minLog + (maxLog - minLog) * x);

        // Volume inversé (haut = fort)
        const vol = (1 - y) * this._params.volume;

        // Appliquer avec portamento
        this.oscillator.frequency.rampTo(freq, this._params.portamento);
        this.volumeGain.gain.rampTo(vol, 0.05);

        this._currentFreq = freq;
        this._currentVol = vol;
    }

    /**
     * Retourne la note la plus proche de la fréquence actuelle
     */
    getCurrentNote() {
        const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const midiNote = Math.round(12 * Math.log2(this._currentFreq / 440) + 69);
        const octave = Math.floor(midiNote / 12) - 1;
        const noteName = noteNames[midiNote % 12];
        return `${noteName}${octave}`;
    }

    getCurrentFrequency() {
        return Math.round(this._currentFreq);
    }

    // Setters
    setWaveform(wave) {
        this._params.waveform = wave;
        this.oscillator.type = wave;
    }

    setVibrato(rate) {
        this._params.vibrato = rate;
        this.vibrato.frequency.value = rate;
    }

    setVibratoAmount(amount) {
        this._params.vibratoAmount = amount;
        this.vibratoGain.gain.value = amount * 15; // Max ±15 Hz de modulation
    }

    setPortamento(time) {
        this._params.portamento = time;
    }

    setVolume(vol) {
        this._params.volume = vol;
        this.outputGain.gain.value = vol;
    }

    setMinFreq(freq) {
        this._params.minFreq = freq;
    }

    setMaxFreq(freq) {
        this._params.maxFreq = freq;
    }

    getParams() {
        return { ...this._params };
    }

    setParams(params) {
        Object.entries(params).forEach(([key, value]) => {
            const setter = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
            if (typeof this[setter] === 'function') {
                this[setter](value);
            }
        });
    }

    dispose() {
        this.stop();
        this.oscillator.dispose();
        this.vibrato.dispose();
        this.vibratoGain.dispose();
        this.volumeGain.dispose();
        this.filter.dispose();
        this.outputGain.dispose();
    }
}
